import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { WeeklyWrap } from "@/components/WeeklyWrap";

/**
 * Owns the Weekly Wrapped lifecycle:
 *   • fetches the recap for users with `wrap:read`,
 *   • AUTO-OPENS on a Monday (SAST) if the user hasn't seen this week yet,
 *   • marks it seen server-side so it won't re-pop,
 *   • exposes a small "Weekly Wrapped" launcher so it's replayable anytime.
 */
export function WeeklyWrapController() {
  const { hasPerm, isMaster } = useAuth();
  const allowed = isMaster || hasPerm?.("wrap:read");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/weekly-wrap");
        if (!alive) return;
        setData(res.data);
        if (res.data?.is_monday && !res.data?.already_seen) setOpen(true);
      } catch (_e) { /* silent — never block the app on the recap */ }
    })();
    return () => { alive = false; };
  }, [allowed]);

  const markSeen = useCallback(async () => {
    setData((d) => (d ? { ...d, already_seen: true } : d));
    try { await api.post("/weekly-wrap/seen"); } catch (_e) { /* best effort */ }
  }, []);

  if (!allowed || !data) return null;

  return (
    <WeeklyWrap data={data} open={open} onClose={() => setOpen(false)} onSeen={markSeen} />
  );
}
